/**
 * Hook for building and working with citation mappings.
 */

import { useMemo } from 'react';
import type {
  Citation,
  ConfidenceScores,
  ExtendAIExtractionOutput,
} from '../types';

/**
 * Result of building citation mappings from extraction output.
 */
interface CitationMappings {
  /** Map from field path to its citations */
  fieldToCitations: Map<string, Citation[]>;
  /** Map from citation to its field path (for reverse lookup) */
  citationToField: Map<Citation, string>;
  /** Map from field path to its confidence scores */
  fieldToConfidence: Map<string, ConfidenceScores>;
  /** All citations flattened into a single array */
  allCitations: Citation[];
}

/**
 * Build citation mappings from Extend AI extraction output.
 */
export function buildCitationMappings(
  extractionOutput: ExtendAIExtractionOutput | undefined | null,
): CitationMappings {
  const fieldToCitations = new Map<string, Citation[]>();
  const citationToField = new Map<Citation, string>();
  const fieldToConfidence = new Map<string, ConfidenceScores>();
  const allCitations: Citation[] = [];

  if (!extractionOutput?.metadata) {
    return {
      fieldToCitations,
      citationToField,
      fieldToConfidence,
      allCitations,
    };
  }

  for (const [fieldPath, metadata] of Object.entries(
    extractionOutput.metadata,
  )) {
    // Store confidence scores
    if (
      metadata.ocrConfidence !== undefined ||
      metadata.logprobsConfidence !== undefined
    ) {
      fieldToConfidence.set(fieldPath, {
        ocrConfidence: metadata.ocrConfidence,
        logprobsConfidence: metadata.logprobsConfidence,
      });
    }

    // Process citations
    if (metadata.citations && metadata.citations.length > 0) {
      const normalizedCitations: Citation[] = metadata.citations
        .filter(
          (c) => c.page !== undefined && c.polygon && c.polygon.length > 0,
        )
        .map((c) => ({
          page: c.page!,
          referenceText: c.referenceText,
          polygon: c.polygon!,
          pageWidth: c.pageWidth,
          pageHeight: c.pageHeight,
        }));

      if (normalizedCitations.length > 0) {
        fieldToCitations.set(fieldPath, normalizedCitations);

        // Reverse mapping and collect all
        for (const citation of normalizedCitations) {
          citationToField.set(citation, fieldPath);
          allCitations.push(citation);
        }
      }
    }
  }

  return { fieldToCitations, citationToField, fieldToConfidence, allCitations };
}

/**
 * Hook for working with citation mappings.
 */
export function useCitationMapping(
  extractionOutput: ExtendAIExtractionOutput | undefined | null,
) {
  const mappings = useMemo(
    () => buildCitationMappings(extractionOutput),
    [extractionOutput],
  );

  return {
    ...mappings,
    getCitationsForField: (fieldPath: string): Citation[] => {
      return mappings.fieldToCitations.get(fieldPath) ?? [];
    },
    getFieldForCitation: (citation: Citation): string | null => {
      return mappings.citationToField.get(citation) ?? null;
    },
    getConfidenceForField: (fieldPath: string): ConfidenceScores | null => {
      return mappings.fieldToConfidence.get(fieldPath) ?? null;
    },
  };
}

/**
 * Parse a field path to extract array indices.
 * E.g., "line_items[0].quantity" -> { basePath: "line_items", index: 0, subPath: "quantity" }
 */
export function parseFieldPath(fieldPath: string): {
  basePath: string;
  index: number | null;
  subPath: string | null;
} {
  // Match patterns like "line_items[0]" or "line_items[0].quantity"
  const match = fieldPath.match(/^([^[]+)\[(\d+)\](?:\.(.+))?$/);

  if (match) {
    return {
      basePath: match[1],
      index: parseInt(match[2], 10),
      subPath: match[3] ?? null,
    };
  }

  return {
    basePath: fieldPath,
    index: null,
    subPath: null,
  };
}

/**
 * Build a field path for an array item.
 * E.g., buildFieldPath("line_items", 0, "quantity") -> "line_items[0].quantity"
 */
export function buildFieldPath(
  basePath: string,
  index?: number,
  subPath?: string,
): string {
  let path = basePath;

  if (index !== undefined) {
    path = `${path}[${index}]`;
  }

  if (subPath) {
    path = `${path}.${subPath}`;
  }

  return path;
}

/**
 * Find all citations for a line item row.
 * This collects citations from the row-level path (e.g., "line_items[0]")
 * and all sub-field paths (e.g., "line_items[0].quantity").
 */
export function getCitationsForLineItemRow(
  fieldToCitations: Map<string, Citation[]>,
  basePath: string,
  index: number,
): Citation[] {
  const rowPath = `${basePath}[${index}]`;
  const citations: Citation[] = [];

  for (const [path, pathCitations] of fieldToCitations) {
    if (path === rowPath || path.startsWith(`${rowPath}.`)) {
      citations.push(...pathCitations);
    }
  }

  return citations;
}
